import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useBuzz } from "../lib/BuzzProvider";
import { MeshHuddle } from "../lib/huddle";
import { shortPubkey } from "../lib/identity";

function formatTime(createdAt: number): string {
  return new Date(createdAt * 1000).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ChatPage() {
  const { client, identity, tick } = useBuzz();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [channelName, setChannelName] = useState("");
  const [channelAbout, setChannelAbout] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [huddle, setHuddle] = useState<MeshHuddle | null>(null);
  const [huddlePeers, setHuddlePeers] = useState<string[]>([]);
  const [huddleStatus, setHuddleStatus] = useState<string | null>(null);

  void tick;

  const channels = client.channelList();
  const active = useMemo(
    () => channels.find((c) => c.id === activeId) ?? null,
    [channels, activeId],
  );
  const messages = activeId ? client.messagesFor(activeId) : [];
  const members = activeId ? client.membersFor(activeId) : [];
  const activeHuddle = activeId ? client.huddleFor(activeId) : null;
  const agentLike = members.filter(
    (m) =>
      m.role === "bot" ||
      client.displayName(m.pubkey).toLowerCase().includes("chief") ||
      client.displayName(m.pubkey).toLowerCase().includes("agent"),
  );

  useEffect(() => {
    if (!activeId && channels.length > 0) {
      setActiveId(channels[0]!.id);
    }
  }, [channels, activeId]);

  useEffect(() => {
    client.selectChannel(activeId);
  }, [client, activeId]);

  useEffect(() => {
    const existing = client.profiles.get(identity.pubkey)?.name;
    if (existing) setDisplayName(existing);
  }, [client, identity.pubkey, tick]);

  useEffect(() => {
    return () => {
      void huddle?.stop();
    };
  }, [huddle]);

  const onCreateChannel = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await client.createChannel(channelName, channelAbout, "open");
      setChannelName("");
      setChannelAbout("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onSend = async (event: FormEvent) => {
    event.preventDefault();
    if (!activeId) return;
    setBusy(true);
    setError(null);
    try {
      const mentions = agentLike.map((m) => m.pubkey);
      await client.sendMessage(activeId, draft, mentions.length ? mentions : []);
      setDraft("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onSaveProfile = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await client.publishProfile(displayName);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const startHuddle = async () => {
    if (!activeId) return;
    setBusy(true);
    setError(null);
    try {
      await client.startHuddleAnnouncement(activeId);
      const mesh = new MeshHuddle(activeId, displayName || "Operator", identity.pubkey);
      mesh.onPeersChanged = setHuddlePeers;
      mesh.onStatus = setHuddleStatus;
      await mesh.start();
      setHuddle(mesh);
      await client.sendMessage(
        activeId,
        "[huddle] Voice huddle started (Cloudflare mesh). Join from this channel.",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const leaveHuddle = async () => {
    if (!activeId) return;
    setBusy(true);
    try {
      await huddle?.stop();
      setHuddle(null);
      setHuddlePeers([]);
      await client.endHuddleAnnouncement(activeId);
      setHuddleStatus(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const joinExistingHuddle = async () => {
    if (!activeId) return;
    setBusy(true);
    setError(null);
    try {
      const mesh = new MeshHuddle(activeId, displayName || "Operator", identity.pubkey);
      mesh.onPeersChanged = setHuddlePeers;
      mesh.onStatus = setHuddleStatus;
      await mesh.start();
      setHuddle(mesh);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="workspace">
      <section className="channel-rail">
        <div className="rail-head">
          <h1>Channels</h1>
          <span className={`badge ${client.state === "ready" ? "ok" : "warn"}`}>
            {client.state}
          </span>
        </div>
        <p className="muted rail-status">{client.statusText}</p>
        <div className="channel-list">
          {channels.length === 0 ? (
            <p className="muted">No channels yet. Create #trading for agents.</p>
          ) : (
            channels.map((channel) => (
              <button
                key={channel.id}
                type="button"
                className={`channel-item ${channel.id === activeId ? "active" : ""}`}
                onClick={() => setActiveId(channel.id)}
              >
                <strong>#{channel.name}</strong>
                <span>
                  {client.huddleFor(channel.id) ? "huddle · " : ""}
                  {channel.about || channel.id.slice(0, 8)}
                </span>
              </button>
            ))
          )}
        </div>
        <form className="stack create-channel" onSubmit={onCreateChannel}>
          <h2>New channel</h2>
          <label className="field">
            Name
            <input
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              placeholder="trading"
              required
            />
          </label>
          <label className="field">
            Purpose
            <input
              value={channelAbout}
              onChange={(e) => setChannelAbout(e.target.value)}
              placeholder="Grow accounts with agents"
            />
          </label>
          <button className="btn primary" type="submit" disabled={busy || client.state !== "ready"}>
            Create channel
          </button>
        </form>
      </section>

      <section className="thread-pane">
        {active ? (
          <>
            <header className="thread-head">
              <div>
                <h1>#{active.name}</h1>
                <p>{active.about || "Buzz channel · humans and agents"}</p>
              </div>
              <div className="row">
                <button
                  className="btn"
                  type="button"
                  disabled={busy || client.state !== "ready"}
                  onClick={() => void client.joinChannel(active.id)}
                >
                  Join
                </button>
                {huddle ? (
                  <button className="btn" type="button" disabled={busy} onClick={() => void leaveHuddle()}>
                    Leave huddle
                  </button>
                ) : activeHuddle ? (
                  <button
                    className="btn primary"
                    type="button"
                    disabled={busy}
                    onClick={() => void joinExistingHuddle()}
                  >
                    Join huddle
                  </button>
                ) : (
                  <button
                    className="btn primary"
                    type="button"
                    disabled={busy || client.state !== "ready"}
                    onClick={() => void startHuddle()}
                  >
                    Start huddle
                  </button>
                )}
              </div>
            </header>
            {(huddle || activeHuddle) && (
              <div className="huddle-bar">
                <span className="badge ok">Huddle live</span>
                <span className="muted">
                  {huddleStatus || "Cloudflare mesh · stun.cloudflare.com"}
                  {huddlePeers.length ? ` · ${huddlePeers.length} remote peer(s)` : ""}
                </span>
              </div>
            )}
            <div className="message-list">
              {messages.length === 0 ? (
                <p className="muted empty-thread">
                  No messages yet. Create #trading, start the VPS chief-agent, and chat here.
                </p>
              ) : (
                messages.map((msg) => (
                  <article className="message" key={msg.id}>
                    <div className="message-meta">
                      <strong>{client.displayName(msg.pubkey)}</strong>
                      <span>{shortPubkey(msg.pubkey)}</span>
                      <span>{formatTime(msg.createdAt)}</span>
                    </div>
                    <p>{msg.content}</p>
                  </article>
                ))
              )}
            </div>
            <form className="composer" onSubmit={onSend}>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={`Message #${active.name}${agentLike.length ? " (agents auto-mentioned)" : ""}`}
                rows={3}
                disabled={client.state !== "ready"}
              />
              <button className="btn primary" type="submit" disabled={busy || !draft.trim()}>
                Send
              </button>
            </form>
          </>
        ) : (
          <div className="empty-thread center">
            <h1>Buzz workspace</h1>
            <p className="muted">
              Create a channel to collaborate with Chief Trades agents in the same room.
            </p>
          </div>
        )}
        {error ? <p className="error composer-error">{error}</p> : null}
      </section>

      <aside className="member-rail">
        <div className="rail-head">
          <h2>Members</h2>
        </div>
        <ul className="member-list">
          {members.length === 0 ? (
            <li className="muted">Member list loads after join / relay discovery.</li>
          ) : (
            members.map((member) => (
              <li key={member.pubkey}>
                <strong>{client.displayName(member.pubkey)}</strong>
                <span>{shortPubkey(member.pubkey)}</span>
                {member.role ? <em>{member.role}</em> : null}
              </li>
            ))
          )}
        </ul>
        <div className="stack identity-card">
          <h2>Agents</h2>
          {agentLike.length === 0 ? (
            <p className="muted">
              No agent-tagged members yet. Deploy chief-agent on the VPS — it creates/joins
              #trading and posts with Workers AI.
            </p>
          ) : (
            <ul className="member-list">
              {agentLike.map((a) => (
                <li key={a.pubkey}>
                  <strong>{client.displayName(a.pubkey)}</strong>
                  <span>{shortPubkey(a.pubkey)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <form className="stack identity-card" onSubmit={onSaveProfile}>
          <h2>You</h2>
          <p className="muted mono">{shortPubkey(identity.pubkey)}</p>
          <label className="field">
            Display name
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Operator"
            />
          </label>
          <button className="btn" type="submit" disabled={busy || client.state !== "ready"}>
            Publish profile
          </button>
        </form>
      </aside>
    </div>
  );
}
